import apiClient from './apiClient';
import { AxiosResponse } from 'axios';

export interface Session {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  acceptingEnrollment: boolean;
  basePrice: number; // Will be parsed as number from string in components
  enrollmentType: 'individual' | 'team' | 'both';
  createdAt: string;
  updatedAt: string;
}

export interface ProblemGroup {
  id: number;
  sessionId: number;
  title: string;
  description: string;
  groupOrder: number;
  startDate?: string;
  endDate?: string;
  hintsVideoUrl?: string;
  hintsVideoReleaseDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProblemOption {
  id: string | number;
  problemId?: number;
  optionText: string;
  isCorrect: boolean;
  optionOrder: number;
}

export interface Problem {
  id: number;
  sessionId: number;
  groupId: number;
  problemStatement: string;
  difficultyLevel: number;
  correctSolution: string;
  imagePath: string | null;
  problemType?: 'open_ended' | 'multiple_choice';
  options?: ProblemOption[];
  createdAt: string;
  updatedAt: string;
}

export interface SessionWithGroups extends Session {
  problemGroups: ProblemGroup[];
}

export interface ProblemGroupWithProblems extends ProblemGroup {
  problems: Problem[];
}

export interface ActiveSessionData {
  session: Session;
  problemGroups: ProblemGroup[];
  activeGroup: ProblemGroup | null;
}

// Session CRUD operations
export const getAllSessions = async (): Promise<Session[]> => {
  try {
    const response: AxiosResponse<Session[]> = await apiClient.get('/api/sessions');
    return response.data;
  } catch (error) {
    console.error('Error fetching sessions:', error);
    throw error;
  }
};

// Get sessions that are accepting enrollment
export const getSessionsAcceptingEnrollment = async (): Promise<Session[]> => {
  try {
    const response: AxiosResponse<Session[]> = await apiClient.get('/api/sessions?accepting=true');
    return response.data.filter(session => session.acceptingEnrollment);
  } catch (error) {
    console.error('Error fetching sessions accepting enrollment:', error);
    throw error;
  }
};

export const getSessionById = async (id: number): Promise<Session> => {
  try {
    const response: AxiosResponse<Session> = await apiClient.get(`/api/sessions/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching session ${id}:`, error);
    throw error;
  }
};

// Alias for getSessionById to match the name used in components
export const getSessionDetails = getSessionById;

export const getSessionWithGroups = async (id: number): Promise<SessionWithGroups> => {
  try {
    const response: AxiosResponse<SessionWithGroups> = await apiClient.get(`/api/sessions/${id}/groups`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching session ${id} with groups:`, error);
    throw error;
  }
};

export const createSession = async (sessionData: Omit<Session, 'id' | 'basePrice' | 'createdAt' | 'updatedAt'> & { basePrice?: number }): Promise<Session> => {
  try {
    const response: AxiosResponse<Session> = await apiClient.post('/api/sessions', sessionData);

    if (!response.data) {
      console.error('[SessionService] Warning: Created session response is null or empty');
    }

    return response.data;
  } catch (error) {
    console.error('[SessionService] Error creating session:', error);
    throw error;
  }
};

export const updateSession = async (id: number, sessionData: Partial<Omit<Session, 'id' | 'createdAt' | 'updatedAt'>> & { basePrice?: number }): Promise<Session> => {
  try {
    const response: AxiosResponse<Session> = await apiClient.patch(`/api/sessions/${id}`, sessionData);
    return response.data;
  } catch (error) {
    console.error(`Error updating session ${id}:`, error);
    throw error;
  }
};

// Update session price
export const updateSessionPrice = async (id: number, basePrice: number): Promise<Session> => {
  try {
    const response: AxiosResponse<{ message: string, session: Session }> = await apiClient.put(`/api/sessions/${id}/price`, { basePrice });
    return response.data.session;
  } catch (error) {
    console.error('Error updating session price:', error);
    throw error;
  }
};

// Active session operations
export const getActiveSession = async (): Promise<ActiveSessionData | null> => {
  try {
    const response: AxiosResponse<ActiveSessionData> = await apiClient.get('/api/sessions/active/current');

    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response && err.response.status === 404) {
      return null;
    }
    console.error('[SessionService] Error fetching active session:', error);
    throw error;
  }
};

export const setActiveSession = async (sessionId: number): Promise<{ message: string; session: Session }> => {
  try {
    const response: AxiosResponse<{ message: string; session: Session }> = await apiClient.post('/api/sessions/active/set', { sessionId });

    return response.data;
  } catch (error) {
    console.error(`[SessionService] Error setting active session ${sessionId}:`, error);
    throw error;
  }
};

export const deleteSession = async (sessionId: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/sessions/${sessionId}`);
  } catch (error) {
    console.error(`Error deleting session ${sessionId}:`, error);
    throw error;
  }
};

export const transferProblemGroupsToSession = async (
  fromSessionId: number, 
  toSessionId: number
): Promise<void> => {
  try {
    await apiClient.post(`/api/problem-groups/transfer`, {
      fromSessionId,
      toSessionId
    });
  } catch (error) {
    console.error(`Error transferring problem groups from session ${fromSessionId} to ${toSessionId}:`, error);
    throw error;
  }
};

// Problem group CRUD operations
export const getAllProblemGroups = async (): Promise<ProblemGroup[]> => {
  try {
    const response: AxiosResponse<ProblemGroup[]> = await apiClient.get('/api/problem-groups');
    return response.data;
  } catch (error) {
    console.error('Error fetching problem groups:', error);
    throw error;
  }
};

export const getProblemGroupById = async (id: number): Promise<ProblemGroup> => {
  try {
    const response: AxiosResponse<ProblemGroup> = await apiClient.get(`/api/problem-groups/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problem group ${id}:`, error);
    throw error;
  }
};

export const getProblemGroupsBySession = async (sessionId: number): Promise<ProblemGroup[]> => {
  try {
    const response: AxiosResponse<ProblemGroup[]> = await apiClient.get(`/api/problem-groups/session/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problem groups for session ${sessionId}:`, error);
    throw error;
  }
};

export const getLiveProblemGroupsBySession = async (sessionId: number): Promise<ProblemGroup[]> => {
  try {
    const response: AxiosResponse<ProblemGroup[]> = await apiClient.get(`/api/problem-groups/session/${sessionId}/live`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching live problem groups for session ${sessionId}:`, error);
    throw error;
  }
};

export const getValidProblemGroupsForSession = async (sessionId: number): Promise<ProblemGroup[]> => {
  try {
    const response: AxiosResponse<ProblemGroup[]> = await apiClient.get(`/api/problems/valid-groups/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching valid problem groups for session ${sessionId}:`, error);
    throw error;
  }
};

export const createProblemGroup = async (groupData: Omit<ProblemGroup, 'id' | 'createdAt' | 'updatedAt'>): Promise<ProblemGroup> => {
  try {
    const response: AxiosResponse<ProblemGroup> = await apiClient.post('/api/problem-groups', groupData);
    return response.data;
  } catch (error) {
    console.error('Error creating problem group:', error);
    throw error;
  }
};

export const updateProblemGroup = async (id: number, groupData: Partial<Omit<ProblemGroup, 'id' | 'createdAt' | 'updatedAt'>>): Promise<ProblemGroup> => {
  try {
    // Backend exposes PATCH (partial update) for problem groups; PUT 404s.
    const response: AxiosResponse<{ message: string; group: ProblemGroup }> = await apiClient.patch(`/api/problem-groups/${id}`, groupData);
    return response.data.group;
  } catch (error) {
    console.error(`Error updating problem group ${id}:`, error);
    throw error;
  }
};

export const deleteProblemGroup = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/problem-groups/${id}`);
  } catch (error) {
    console.error(`Error deleting problem group ${id}:`, error);
    throw error;
  }
};

// Active problem group operations
export const getActiveProblemGroup = async (): Promise<ProblemGroup | null> => {
  try {
    const response: AxiosResponse<ProblemGroup> = await apiClient.get('/api/problem-groups/active/current');
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response && err.response.status === 404) {
      return null;
    }
    console.error('Error fetching active problem group:', error);
    throw error;
  }
};

export const setActiveProblemGroup = async (groupId: number): Promise<{ message: string; group: ProblemGroup }> => {
  try {
    const response: AxiosResponse<{ message: string; group: ProblemGroup }> = await apiClient.post('/api/problem-groups/active/set', { groupId });
    return response.data;
  } catch (error) {
    console.error(`Error setting active problem group ${groupId}:`, error);
    throw error;
  }
};

// Problem CRUD operations
export const getAllProblems = async (): Promise<Problem[]> => {
  try {
    const response: AxiosResponse<Problem[]> = await apiClient.get('/api/problems');
    return response.data;
  } catch (error) {
    console.error('Error fetching problems:', error);
    throw error;
  }
};

export const getProblemById = async (id: number): Promise<Problem> => {
  try {
    const response: AxiosResponse<Problem> = await apiClient.get(`/api/problems/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problem ${id}:`, error);
    throw error;
  }
};

export const getProblemsBySession = async (sessionId: number): Promise<Problem[]> => {
  try {
    const response: AxiosResponse<Problem[]> = await apiClient.get(`/api/problems/session/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problems for session ${sessionId}:`, error);
    throw error;
  }
};

export const getProblemsByGroup = async (groupId: number): Promise<Problem[]> => {
  try {
    const response: AxiosResponse<Problem[]> = await apiClient.get(`/api/problems/group/${groupId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching problems for group ${groupId}:`, error);
    throw error;
  }
};

export const getProblemGroupWithProblems = async (groupId: number): Promise<ProblemGroupWithProblems> => {
  try {
    const [group, problems] = await Promise.all([
      getProblemGroupById(groupId),
      getProblemsByGroup(groupId),
    ]);

    // Map response to ensure camelCase property names and include problemType and options
    const camelCasedProblems = problems.map(problem => {
      // Handle both open-ended and multiple-choice problems
      return {
        id: problem.id,
        sessionId: problem.sessionId,
        groupId: problem.groupId,
        problemStatement: problem.problemStatement,
        difficultyLevel: problem.difficultyLevel,
        correctSolution: problem.correctSolution,
        imagePath: problem.imagePath,
        // Include problemType with default to open_ended for backward compatibility
        problemType: problem.problemType || 'open_ended',
        // Include options array for multiple-choice problems
        options: problem.options || [],
        createdAt: problem.createdAt,
        updatedAt: problem.updatedAt
      };
    });
    
    return {
      ...group,
      problems: camelCasedProblems
    };
  } catch (error) {
    console.error(`Error fetching problem group with problems ${groupId}:`, error);
    throw error;
  }
};

export const createProblem = async (problemData: Omit<Problem, 'id' | 'createdAt' | 'updatedAt'>): Promise<Problem> => {
  try {
    const response: AxiosResponse<Problem> = await apiClient.post('/api/problems', problemData);
    return response.data;
  } catch (error) {
    console.error('Error creating problem:', error);
    throw error;
  }
};

export const createMultipleProblems = async (problemsData: Omit<Problem, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<Problem[]> => {
  try {
    const response: AxiosResponse<{ message: string; problems: Problem[] }> = await apiClient.post('/api/problems/bulk', { problems: problemsData });
    return response.data.problems;
  } catch (error) {
    console.error('Error creating multiple problems:', error);
    throw error;
  }
};

export const updateProblem = async (id: number, problemData: Partial<Omit<Problem, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Problem> => {
  try {
    const response: AxiosResponse<Problem> = await apiClient.put(`/api/problems/${id}`, problemData);
    return response.data;
  } catch (error) {
    console.error(`Error updating problem ${id}:`, error);
    throw error;
  }
};

export const deleteProblem = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/api/problems/${id}`);
  } catch (error) {
    console.error(`Error deleting problem ${id}:`, error);
    throw error;
  }
};

// Hint video interfaces and methods

export interface HintVideo {
  groupId: number;
  title: string;
  videoUrl: string;
  sessionId: number;
  sessionTitle: string;
}

export interface GroupHintVideo {
  videoUrl: string;
  title: string;
}

export const getAvailableHintVideos = async (): Promise<HintVideo[]> => {
  try {
    const response: AxiosResponse<HintVideo[]> = await apiClient.get('/api/problem-groups/videos/available');
    return response.data;
  } catch (error) {
    console.error('Error fetching available hint videos:', error);
    throw error;
  }
};

export const getGroupHintVideo = async (groupId: number): Promise<GroupHintVideo | null> => {
  try {
    const response: AxiosResponse<GroupHintVideo> = await apiClient.get(`/api/problem-groups/${groupId}/video`);
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response && err.response.status === 404) {
      return null;
    }
    console.error(`Error fetching hint video for group ${groupId}:`, error);
    throw error;
  }
};